import React, { useEffect, useRef, ReactElement } from 'react';
import { getShadowTheme } from '../../../style';
import Container from '../Container';
import FooterNavLink from './FooterNavLink';
import { navbarIconsFooter } from '../../../atoms/icons/materialIcons';
import styled from '@emotion/styled';
import FooterWYPLogo from './FooterWYPLogo';
import {
  getBrowserDetails,
  iOSwithHomeBtn,
  ipadNoHomeButton,
  tabletWidth,
  triggerTracking,
} from '../../../utils/helpers';
import {
  sendDashboardNavbarSearch,
  sendDashboardNavbarInterests,
  sendDashboardNavbarOffers,
  sendDashboardNavbarFavourites,
} from '../../../actions/dashboardTrackAction';
import { useApp } from '../../../contexts/AppContext';
import { IFooterProps } from './types/Footer';
import breakpoints from '../../../theme/material/breakpoints';

const containerStyles = {
  display: 'flex',
  padding: '0 0',
  [breakpoints.down(639.05)]: {
    justifyContent: 'space-evenly',
  },
};

const StyledCenterSpacing = styled('div')({
  padding: 38,
  flexGrow: 1,
  [breakpoints.down(361.05)]: {
    padding: 0,
    width: '100%',
  },
});

const heightLength = (homeBtn: boolean) => (homeBtn ? 60 : 63);
const bottomLength = (homeBtn: boolean) => (homeBtn ? 25 : 0);

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const SiteFooter = styled('div')(({ isDarkMode }: { isDarkMode: boolean }) => ({
  boxShadow: getShadowTheme().footer,
  backgroundColor: isDarkMode ? '#212121' : 'transparent',
  background: isDarkMode ? '#212121' : 'linear-gradient(180deg, #E4F8F6 0%, #FFF 100%)',
  verticalAlign: 'middle',
  height:
    getBrowserDetails().isIos && !tabletWidth && getBrowserDetails().isStandaloneMode
      ? heightLength(!iOSwithHomeBtn())
      : heightLength(ipadNoHomeButton),
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 995,
  paddingBottom:
    getBrowserDetails().isIos && !tabletWidth && getBrowserDetails().isStandaloneMode
      ? bottomLength(!iOSwithHomeBtn())
      : bottomLength(ipadNoHomeButton),
  '& #header-nav-container + a + span': {
    color: 'red',
  },
}));

const NavBar = styled('div')`
  padding-top: 60px;
`;

const renderIcon = (isDarkMode: boolean, icon: any, status: 'default' | 'active') => {
  switch (status) {
    case 'default':
      return isDarkMode ? icon.dark : icon.default;
    case 'active':
      return isDarkMode ? icon.darkActive : icon.active;
    default:
      return;
  }
};

const setCorrectPageAction = (_e: Event, page: 'Search' | 'Favourites' | 'Offers' | 'Interests') => {
  switch (page) {
    case 'Search':
      sendDashboardNavbarSearch();
      break;
    case 'Favourites':
      sendDashboardNavbarFavourites();
      break;
    case 'Offers':
      sendDashboardNavbarOffers();
      break;
    case 'Interests':
      sendDashboardNavbarInterests();
      break;
    default:
      return;
  }
};

const Footer = ({
  newFavorites,
  newOffers,
  messages,
  pathname,
  isDarkMode,
  requiredVerification,
}: IFooterProps): ReactElement => {
  const noNavPages =
    pathname.indexOf('/payment') > -1 || pathname.indexOf('/packages') > -1 || pathname.indexOf('/billing') > -1;

  const visitSearch = useRef(null);
  const visitFavourites = useRef(null);
  const visitOffers = useRef(null);
  const visitInterests = useRef(null);
  const { siteFooterRef } = useApp();

  useEffect(() => {
    triggerTracking('Search', visitSearch, setCorrectPageAction);
    triggerTracking('Interests', visitFavourites, setCorrectPageAction);
    triggerTracking('Offers', visitOffers, setCorrectPageAction);
    triggerTracking('Interests', visitInterests, setCorrectPageAction);
  }, []);

  if (!requiredVerification && noNavPages) return null;

  return (
    <NavBar>
      <SiteFooter
        id="site-footer"
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        isDarkMode={isDarkMode}
        ref={siteFooterRef}
      >
        <FooterWYPLogo isDarkMode={isDarkMode} currentPath={pathname} activePathPattern={/.*dashboard/} />
        <Container fullWidth css={containerStyles}>
          <FooterNavLink
            footerRef={visitSearch}
            to="/search"
            currentPath={pathname}
            activePathPattern={/.*search/}
            iconComponent={isDarkMode ? navbarIconsFooter.search.dark : navbarIconsFooter.search.default}
            activeIconComponent={isDarkMode ? navbarIconsFooter.search.darkActive : navbarIconsFooter.search.active}
            isDarkMode={isDarkMode}
          />
          <FooterNavLink
            footerRef={visitFavourites}
            to={'/interests'}
            currentPath={pathname}
            activePathPattern={/.*interests/}
            iconComponent={renderIcon(isDarkMode, navbarIconsFooter.interests, 'default')}
            activeIconComponent={renderIcon(isDarkMode, navbarIconsFooter.interests, 'active')}
            notification={newFavorites}
            isDarkMode={isDarkMode}
          />
          <StyledCenterSpacing />
          <FooterNavLink
            footerRef={visitOffers}
            to="/offers/new"
            currentPath={pathname}
            activePathPattern={/^.offers/}
            iconComponent={isDarkMode ? navbarIconsFooter.offers.dark : navbarIconsFooter.offers.default}
            activeIconComponent={isDarkMode ? navbarIconsFooter.offers.darkActive : navbarIconsFooter.offers.active}
            notification={newOffers}
            isDarkMode={isDarkMode}
          />
          <FooterNavLink
            footerRef={visitInterests}
            to="/mail"
            currentPath={pathname}
            activePathPattern={/^.mail$/}
            iconComponent={isDarkMode ? navbarIconsFooter.mail.dark : navbarIconsFooter.mail.default}
            activeIconComponent={isDarkMode ? navbarIconsFooter.mail.darkActive : navbarIconsFooter.mail.active}
            notification={messages}
            isDarkMode={isDarkMode}
          />
        </Container>
      </SiteFooter>
    </NavBar>
  );
};

export default Footer;
